// Money helpers (§C.8).
//
// Internal representation: all money values in LeaseFormState are stored as
// integer cents (e.g. $1,234.56 → 123456). This module provides conversions
// between cents and the display/input formats used in the UI.
//
// No floating-point math on monetary values: multiply/divide by 100 then
// round to integer cents to avoid IEEE-754 drift.

/**
 * Format an integer cents amount for display.
 * Uses Intl.NumberFormat for locale-correct currency formatting.
 *
 * @example
 *   formatMoneyCents(123456) === '$1,234.56'
 *   formatMoneyCents(0)      === '$0.00'
 *   formatMoneyCents(-500)   === '-$5.00'
 */
export function formatMoneyCents(cents: number): string {
  // Intl.NumberFormat handles negative, zero, and large values correctly.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * Parse a dollar string (possibly with `$`, commas, spaces) to integer cents.
 * Returns 0 for empty, non-numeric, malformed, or non-finite input.
 *
 * Uses Number() rather than parseFloat() to reject partial input — parseFloat
 * accepts trailing garbage ("1abc" → 1), scientific notation overflows
 * ("1e309" → Infinity), and multi-decimal ("1.2.3" → 1.2). Number() returns
 * NaN for those; Number.isFinite() also catches Infinity. This prevents bad
 * money values from persisting into draft state.
 *
 * @example
 *   parseMoneyDollarsToCents('$1,234.56') === 123456
 *   parseMoneyDollarsToCents('50')        === 5000
 *   parseMoneyDollarsToCents('')          === 0
 *   parseMoneyDollarsToCents('abc')       === 0
 *   parseMoneyDollarsToCents('1abc')      === 0  // parseFloat would return 1
 *   parseMoneyDollarsToCents('1.2.3')     === 0  // parseFloat would return 1.2
 *   parseMoneyDollarsToCents('1e309')     === 0  // Infinity rejected
 */
export function parseMoneyDollarsToCents(input: string): number {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (!cleaned) return 0;
  const dollars = Number(cleaned);
  if (!Number.isFinite(dollars)) return 0;
  return Math.round(dollars * 100);
}

/**
 * Convert a numeric dollar value (from a non-string source like AppFolio's
 * mirror JSON) to integer cents. Distinct from `parseMoneyDollarsToCents`,
 * which parses a user-entered string. Both end in `Math.round(... × 100)`
 * to land on integer cents.
 *
 * @example
 *   dollarsNumberToCents(1500)    === 150000
 *   dollarsNumberToCents(1234.56) === 123456
 *   dollarsNumberToCents(null)    === 0
 *   dollarsNumberToCents(NaN)     === 0
 */
export function dollarsNumberToCents(dollars: number | null | undefined): number {
  if (dollars === null || dollars === undefined) return 0;
  if (!Number.isFinite(dollars)) return 0;
  return Math.round(dollars * 100);
}

/**
 * Convert integer cents to a decimal string suitable for a controlled
 * `<input type="number" step="0.01">` element.
 *
 * Returns a two-decimal-place string (e.g. `"50.00"`, `"1234.56"`).
 * Negative values are formatted with a leading `-`.
 *
 * @example
 *   centsToDollarsForInput(5000)   === '50.00'
 *   centsToDollarsForInput(123456) === '1234.56'
 *   centsToDollarsForInput(0)      === '0.00'
 */
export function centsToDollarsForInput(cents: number): string {
  return (cents / 100).toFixed(2);
}
